/**
 * Rep detection from the accelerometer (todo 8). Pure, streaming: `repCounter()` takes the samples as they come
 * (`push(t, x, y, z)`, any rate from about 50 Hz up) and keeps a running count, so the timer screen can show it live.
 *
 * What it looks at: with the phone in a trouser pocket or an armband the signal of a rep is mostly the phone tilting
 * (gravity moving between the axes as the thigh or arm swings), plus some real acceleration. So "gravity removed" means
 * the steady part only: the counter measures against a centre that follows the movement, it keeps the tilt.
 *
 * Steps per 50 ms bin: average the samples of the bin (the bins make every rate look the same), a moving average of
 * 0.25 s against the jitter, then project on the main direction of movement: the largest principal axis of the
 * changes over the last 8 s (the changes, not the values, so the step from rest into the set does not win), found
 * automatically and following the phone if it slips. Its sign is chosen so that the pose at rest is the high end. The
 * centre is halfway between the extremes of the last 1.5 reps. On that one signal a rep is one full cycle: down past
 * −h, back up past +h (hysteresis h relative to the size of the reps), so it counts when the rep comes back and a half
 * rep at the end does not count; and at least a minimum time after the rep before, which adapts to the rhythm found
 * (period by autocorrelation, capped by the reps' own gaps), so slow squats and fast swings both work.
 *
 * Stowing the phone or getting down to the floor must not count: the counter "locks in" only after 3 cycles of
 * similar size, at most 8 s apart, whose far ends (the bottom of a squat) are the same pose; then all 3 count at
 * once, and later cycles count as they come if they are not much smaller than the reps before. A profile learned from
 * earlier sets of the exercise (`RepProfile`, see `learnProfile`) gives the axis, the rhythm and the size to expect: the
 * counter starts from those and locks in after 2 cycles of the expected size. There is no calibration step.
 *
 * Known weak spot (synthetic tests): very fast reps whose signal has two humps per rep never lock in (count 0).
 */
import type { Columns, MotionRecording } from "./motion";

/** What earlier sets of an exercise taught about its movement, per exercise and placement. */
export interface RepProfile {
  /** the main direction of movement in the phone's axes (unit vector, sign does not matter) */
  axis: [number, number, number];
  /** typical time from one rep to the next, ms */
  periodMs: number;
  /** typical size of a rep on the axis (peak to peak, m/s²) */
  amplitude: number;
  /** how many sets went into it */
  sets: number;
}

/** Tuning; the defaults are what `repCounter` uses. Exported for the tests and `npm run reps:check`. */
export const REP_DEFAULTS = {
  /** bin width: the signal is averaged into bins of this many ms (20 Hz) */
  binMs: 50,
  /** moving average over this many bins */
  smoothBins: 5,
  /** the window for the axis and the rhythm, ms */
  windowMs: 8000,
  /** the centre of the signal is halfway between the extremes of 1.5 reps, but of at least this many ms */
  centerMinMs: 1000,
  /** ... and of this many ms before a rhythm is known */
  centerStartMs: 1000,
  /** hysteresis: a cycle must go below −h and above +h, h = half this share of the size of recent reps (or of the range) */
  hysteresis: 0.3,
  /** below this peak-to-peak size (m/s²) nothing is a rep: noise and small shifts */
  minAmplitude: 0.8,
  /** shortest possible time between reps, ms (fast jumping jacks) */
  minGapMs: 400,
  /** longest time between the first reps for locking in, ms */
  maxLockGapMs: 8000,
  /** a rep after locking in must be at least this share of the typical rep's size */
  minShare: 0.35,
  /** locking in: the sizes of the first cycles within this factor of each other */
  lockRatio: 2.5,
  /** ... and the gaps between them too */
  lockGapRatio: 3,
  /** ... and their far ends (the pose at the bottom of a rep) within this share of their size of each other */
  farShare: 0.5,
  /** the minimum time between reps is this share of the rhythm found */
  gapShare: 0.4,
};

export type RepTuning = typeof REP_DEFAULTS;

export interface RepCounter {
  /** one accelerometer sample (t in ms, m/s² with gravity); returns the count so far */
  push(t: number, x: number, y: number, z: number): number;
  readonly count: number;
  /** when each counted rep was detected (ms, the samples' clock) */
  readonly times: number[];
  /** the size of each counted rep (peak to peak on the axis, m/s²) */
  readonly amplitudes: number[];
  /** the axis used last (unit vector) */
  readonly axis: [number, number, number];
}

type Vec = [number, number, number];

/** the axis is found from the changes of the smoothed signal over this many bins (100 ms) */
const DIFF_BINS = 2;

const dot = (a: Vec, b: Vec) => a[0] * b[0] + a[1] * b[1] + a[2] * b[2];
const norm = (a: Vec): Vec => {
  const l = Math.hypot(a[0], a[1], a[2]);
  return l > 1e-9 ? [a[0] / l, a[1] / l, a[2] / l] : [0, 1, 0];
};
const mean = (v: Vec[]): Vec => {
  const m: Vec = [0, 0, 0];
  for (const p of v) { m[0] += p[0]; m[1] += p[1]; m[2] += p[2]; }
  return [m[0] / v.length, m[1] / v.length, m[2] / v.length];
};
const median = (v: number[]) => {
  if (!v.length) return 0;
  const s = [...v].sort((a, b) => a - b);
  const m = s.length >> 1;
  return s.length % 2 ? s[m] : (s[m - 1] + s[m]) / 2;
};

/** The largest principal axis of a list of 3-vectors (power iteration, started from `seed`). */
function principal(points: Vec[], seed: Vec): Vec {
  const m = mean(points);
  let xx = 0, xy = 0, xz = 0, yy = 0, yz = 0, zz = 0;
  for (const p of points) {
    const a = p[0] - m[0], b = p[1] - m[1], c = p[2] - m[2];
    xx += a * a; xy += a * b; xz += a * c; yy += b * b; yz += b * c; zz += c * c;
  }
  let v = seed;
  for (let k = 0; k < 12; k++) {
    const w: Vec = [xx * v[0] + xy * v[1] + xz * v[2], xy * v[0] + yy * v[1] + yz * v[2], xz * v[0] + yz * v[1] + zz * v[2]];
    const lambda = Math.hypot(w[0], w[1], w[2]);
    if (lambda < 1e-12) break;
    v = [w[0] / lambda, w[1] / lambda, w[2] / lambda];
  }
  // keep the sign of the seed, so the projection does not flip from one bin to the next
  return dot(v, seed) < 0 ? [-v[0], -v[1], -v[2]] : v;
}

/**
 * The period of a signal in bins by autocorrelation, between `minLag` and `maxLag`: the shortest lag whose normalised
 * correlation is nearly as high as the best one (twice the period correlates about as well), if the best is clear
 * enough (> 0.3); else undefined.
 */
export function period(signal: number[], minLag: number, maxLag: number): number | undefined {
  const n = signal.length;
  const mean = signal.reduce((s, v) => s + v, 0) / n;
  const d = signal.map((v) => v - mean);
  const energy = d.reduce((s, v) => s + v * v, 0);
  if (energy < 1e-9) return undefined;
  let best = 0, bestLag = 0;
  const top = Math.min(maxLag, Math.floor(n * 0.75));
  const r: number[] = [];
  for (let lag = 0; lag <= top; lag++) {
    let s = 0;
    for (let i = lag; i < n; i++) s += d[i] * d[i - lag];
    // normalised by the overlap, so long lags are not penalised for being short
    r.push((s / (n - lag)) / (energy / n));
  }
  const peaks: number[] = [];
  for (let lag = Math.max(1, minLag); lag < top; lag++) {
    // a local maximum
    if (r[lag] >= r[lag - 1] && r[lag] >= r[lag + 1]) {
      peaks.push(lag);
      if (r[lag] > best) { best = r[lag]; bestLag = lag; }
    }
  }
  if (best <= 0.3) return undefined;
  return peaks.find((lag) => r[lag] >= 0.8 * best) ?? bestLag;
}

/** A new counter, starting from a learned profile if there is one. */
export function repCounter(profile?: RepProfile, tuning: RepTuning = REP_DEFAULTS): RepCounter {
  const T = tuning;
  const windowBins = Math.round(T.windowMs / T.binMs);
  // the current bin
  let binStart: number | undefined;
  let acc: Vec = [0, 0, 0];
  let accN = 0;
  /** the averaged bins (for the moving average), the smoothed points and their changes over DIFF_BINS (for the window) */
  const bins: Vec[] = [];
  const smooth: Vec[] = [];
  const diffs: Vec[] = [];
  /** the projected signal of the window, for the rhythm */
  const proj: number[] = [];
  let axis: Vec = profile ? norm(profile.axis) : [0, 1, 0];
  let binsSeen = 0;
  // the hysteresis state machine; the axis is turned so that rest is the high end: a rep counts when it comes back,
  // and a half rep at the end of the set does not count
  let state: "low" | "high" = "high";
  let lowMin = Infinity;
  /** the smoothed point at lowMin: the far end of the rep */
  let lowPoint: Vec = [0, 0, 0];
  let highMax = -Infinity;
  /** the smoothed points at highMax, in this and in the last high phase (rest, before the first rep) */
  let highPoint: Vec | undefined;
  let lastHighPoint: Vec | undefined;
  /** the current high phase has not been counted or rejected yet */
  let open = false;
  /** what the current high phase decided, whose size is still growing */
  let grows: "rep" | "pending" | undefined;
  let lastRepAt = -Infinity;
  /** the first cycles, waiting for enough of them to lock in */
  let pending: { at: number; amp: number; far: Vec }[] = [];
  const lockCycles = profile ? 2 : 3;
  let locked = false;
  /** where the phone rested when the set started (the mean of the first smoothed points) */
  let rest: Vec | undefined;
  /** the axis' sign is set (rest is the high end), done when the movement first gets big */
  let oriented = false;
  let periodBins = profile ? profile.periodMs / T.binMs : undefined;
  const times: number[] = [];
  const amplitudes: number[] = [];

  const typical = () => {
    const recent = amplitudes.slice(-6);
    if (recent.length) return median(recent);
    return profile?.amplitude;
  };

  const accept = (at: number, amp: number) => {
    times.push(at);
    amplitudes.push(amp);
    lastRepAt = at;
  };

  /**
   * The time from rep to rep, ms (Infinity while unknown): the period found by autocorrelation, capped by the median
   * gap of the last reps, since the autocorrelation may find twice the period (pauses between reps).
   */
  const rhythm = () => {
    const gaps = times.slice(-5).map((v, i, a) => (i ? v - a[i - 1] : 0)).slice(1);
    return Math.min(periodBins ? periodBins * T.binMs : Infinity, gaps.length ? median(gaps) : Infinity);
  };

  /** the size a cycle needs to be a rep */
  const needed = () => {
    const ty = locked ? typical() : undefined;
    return Math.max(T.minAmplitude, ty === undefined ? 0 : T.minShare * ty);
  };

  /** a full cycle of size `amp` (big enough) with its far end at `far` came back at `at`: count it, or lock in */
  const cycle = (at: number, amp: number, far: Vec) => {
    const r = rhythm();
    const minGap = Math.max(T.minGapMs, Number.isFinite(r) ? r * T.gapShare : 0);
    if (at - lastRepAt < minGap) return;
    if (locked) {
      accept(at, amp);
      return;
    }
    // locking in: the last cycles (3, or 2 of the size a learned profile expects) alike in size and rhythm
    const list = [...pending.filter((c) => at - c.at <= T.maxLockGapMs * (lockCycles - 1)), { at, amp, far }].slice(-lockCycles);
    pending = list;
    if (list.length < lockCycles) return;
    const amps = list.map((c) => c.amp);
    const gaps = list.slice(1).map((c, i) => c.at - list[i].at);
    const alike = (v: number[], ratio: number) => Math.max(...v) / Math.min(...v) <= ratio;
    // every rep reaches the same pose (the bottom of a squat); stowing the phone or getting down moves it anywhere
    const spreadFar = Math.max(...list.flatMap((a) => list.map((b) => Math.hypot(a.far[0] - b.far[0], a.far[1] - b.far[1], a.far[2] - b.far[2]))));
    const samePose = spreadFar <= T.farShare * median(amps);
    const fits = profile
      ? amps.every((a) => a >= T.minShare * profile.amplitude && a <= T.lockRatio * profile.amplitude)
        && gaps.every((g) => g <= T.maxLockGapMs && g >= profile.periodMs / T.lockRatio)
      : alike(amps, T.lockRatio) && alike(gaps, T.lockGapRatio) && gaps.every((g) => g <= T.maxLockGapMs);
    if (!samePose || !fits) return;
    locked = true;
    for (const c of list) accept(c.at, c.amp);
    pending = [];
  };

  const step = (at: number, v: Vec) => {
    bins.push(v);
    if (bins.length > T.smoothBins) bins.shift();
    const s: Vec = [0, 0, 0];
    for (const b of bins) { s[0] += b[0]; s[1] += b[1]; s[2] += b[2]; }
    s[0] /= bins.length; s[1] /= bins.length; s[2] /= bins.length;
    const before = smooth.length >= DIFF_BINS ? smooth[smooth.length - DIFF_BINS] : undefined;
    smooth.push(s);
    if (smooth.length > windowBins) smooth.shift();
    if (before) {
      diffs.push([s[0] - before[0], s[1] - before[1], s[2] - before[2]]);
      if (diffs.length > windowBins) diffs.shift();
    }
    binsSeen++;
    // the first half second only fills the window
    if (diffs.length < 10) return;
    // the axis from the changes, not the values: a set starting from rest moves the values' mean, and that step
    // would otherwise be the largest direction
    const candidate = principal(diffs, axis);
    // the centre follows the movement: halfway between the lowest and highest point of about one and a half reps
    // (1 s before a rhythm is known), so the signal crosses it in every rep, also right after starting from rest at
    // one end of the movement (a mean would lag there)
    const r = rhythm();
    const cBins = Math.min(smooth.length, Math.round(Math.max(T.centerMinMs, Math.min(T.windowMs, Number.isFinite(r) ? 1.5 * r : T.centerStartMs)) / T.binMs));
    const range = (a: Vec) => {
      let lo = Infinity, hi = -Infinity;
      for (let i = smooth.length - cBins; i < smooth.length; i++) {
        const q = dot(smooth[i], a);
        lo = Math.min(lo, q);
        hi = Math.max(hi, q);
      }
      return { lo, hi };
    };
    let { lo, hi } = range(candidate);
    // a learned axis is kept while the movement is still small (a pause, the start of the set)
    if (!profile || hi - lo >= T.minAmplitude) axis = candidate;
    else ({ lo, hi } = range(axis));
    const spread = hi - lo;
    if (!rest && smooth.length >= 10) rest = mean(smooth.slice(0, 10));
    if (!oriented && rest && spread >= T.minAmplitude) {
      oriented = true;
      // moving away from rest goes down
      if (dot([s[0] - rest[0], s[1] - rest[1], s[2] - rest[2]], axis) > 0) {
        axis = [-axis[0], -axis[1], -axis[2]];
        ({ lo, hi } = range(axis));
      }
    }
    const p = dot(s, axis) - (lo + hi) / 2;
    proj.push(p);
    if (proj.length > windowBins) proj.shift();
    // the rhythm, once a second
    if (binsSeen % 20 === 0 && proj.length >= 40) {
      const found = period(proj, Math.round(T.minGapMs / T.binMs), proj.length);
      if (found) periodBins = found;
    }
    const ty = typical();
    // h from the reps so far, else from the range of the last moments
    const size = Math.max(T.minAmplitude, ty ?? spread);
    const h = T.hysteresis * size / 2;
    if (state === "high" && p < -h) {
      lastHighPoint = highPoint;
      state = "low";
      lowMin = p;
      lowPoint = s;
    } else if (state === "low") {
      if (p < lowMin) { lowMin = p; lowPoint = s; }
      if (p > h) {
        state = "high";
        highMax = p;
        highPoint = s;
        open = true;
      }
    } else if (p > highMax) {
      highMax = p;
      highPoint = s;
    }
    // a high phase decides once: when the cycle (from the far end in the low phase to the higher of the last and this
    // high) is big enough; a slow start, crossing h early with a tiny threshold, is decided later in the phase. The
    // size is measured between the points themselves, not against the moving centre (at rest it sits on the signal).
    if (state !== "high" || !highPoint) return;
    const reach = (q: Vec | undefined) => (q ? dot([q[0] - lowPoint[0], q[1] - lowPoint[1], q[2] - lowPoint[2]], axis) : -Infinity);
    const amp = Math.max(reach(lastHighPoint), reach(highPoint));
    if (open) {
      if (amp >= needed()) {
        open = false;
        const before = times.length;
        cycle(at, amp, lowPoint);
        grows = times.length > before ? "rep" : pending.at(-1)?.at === at ? "pending" : undefined;
      }
    } else if (grows === "rep") {
      // the rest of the phase gives the cycle's full size
      amplitudes[amplitudes.length - 1] = Math.max(amplitudes[amplitudes.length - 1], amp);
    } else if (grows === "pending" && pending.length) {
      pending[pending.length - 1].amp = Math.max(pending[pending.length - 1].amp, amp);
    }
  };

  return {
    push(t, x, y, z) {
      if (binStart === undefined) binStart = t;
      while (t >= binStart + T.binMs) {
        if (accN) {
          step(binStart + T.binMs, [acc[0] / accN, acc[1] / accN, acc[2] / accN]);
        } else if (bins.length) {
          // no sample in this bin (the phone was busy): repeat the last one
          step(binStart + T.binMs, bins[bins.length - 1]);
        }
        acc = [0, 0, 0];
        accN = 0;
        binStart += T.binMs;
      }
      acc[0] += x; acc[1] += y; acc[2] += z;
      accN++;
      return times.length;
    },
    get count() { return times.length; },
    get times() { return times; },
    get amplitudes() { return amplitudes; },
    get axis() { return axis; },
  };
}

/** Runs a counter over the samples between `from` and `to` (ms, the samples' clock). */
export function countReps(accel: Columns, from: number, to: number, profile?: RepProfile, tuning?: RepTuning): RepCounter {
  const c = repCounter(profile, tuning);
  for (let i = 0; i < accel.t.length; i++) {
    const t = accel.t[i];
    if (t < from) continue;
    if (t > to) break;
    c.push(t, accel.x[i], accel.y[i], accel.z[i]);
  }
  return c;
}

/**
 * The profile after a set of `reps` reps (as the owner confirmed them) between `from` and `to`: the axis and the
 * typical size from counting the set, the rhythm from the set's length and the reps. Blended with the earlier profile
 * (each set weighs the same, up to the last 5), so one odd set does not throw it off. Undefined when the set gives
 * nothing to learn (no reps, or nothing counted).
 */
export function learnProfile(accel: Columns, from: number, to: number, reps: number, previous?: RepProfile): RepProfile | undefined {
  if (reps < 2) return previous;
  const c = countReps(accel, from, to);
  if (c.count < 2) return previous;
  const times = c.times;
  const gaps = times.slice(1).map((v, i) => v - times[i]);
  const periodMs = Math.min(median(gaps), (to - from) / reps);
  const amplitude = median(c.amplitudes);
  let axis = norm(c.axis);
  if (!previous) return { axis: canonical(axis), periodMs, amplitude, sets: 1 };
  const w = Math.min(previous.sets, 4);
  const prev = norm(previous.axis);
  if (dot(axis, prev) < 0) axis = [-axis[0], -axis[1], -axis[2]];
  const mix = (a: number, b: number) => (a * w + b) / (w + 1);
  return {
    axis: canonical(norm([mix(prev[0], axis[0]), mix(prev[1], axis[1]), mix(prev[2], axis[2])])),
    periodMs: mix(previous.periodMs, periodMs),
    amplitude: mix(previous.amplitude, amplitude),
    sets: previous.sets + 1,
  };
}

/** The axis with its largest component positive (the sign of an axis means nothing), rounded for storage. */
function canonical(a: Vec): Vec {
  const i = [0, 1, 2].reduce((m, k) => (Math.abs(a[k]) > Math.abs(a[m]) ? k : m), 0);
  const s = a[i] < 0 ? -1 : 1;
  return [a[0], a[1], a[2]].map((v) => Math.round(v * s * 1000) / 1000) as Vec;
}

/**
 * A recording as the rep detection sees it (`npm run reps:check` and the tests): the count in the set window, and
 * the count in the lead-in before it (stowing the phone, which must count 0).
 */
export function countRecording(r: Pick<MotionRecording, "accel" | "setStart" | "setEnd">, profile?: RepProfile): { counted: number; leadIn: number } {
  return {
    counted: countReps(r.accel, r.setStart, r.setEnd, profile).count,
    leadIn: countReps(r.accel, r.accel.t[0] ?? 0, r.setStart, profile).count,
  };
}
